import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  deleteDoc,
  orderBy,
  limit,
  writeBatch,
  getCountFromServer,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { ACCESS_LEVELS } from '../constants/accessLevels';

// ─── Helpers ────────────────────────────────────────────────────────────────

const libraryRef = (coachId) => collection(db, 'coaches', coachId, 'library');
const libraryDocRef = (coachId, itemId) => doc(db, 'coaches', coachId, 'library', itemId);

const SYSTEM_FOLDER_PUZZLES = '__puzzles__';
const SYSTEM_FOLDER_START_HERE = '__start_here__';

function detectFileType(fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (ext === 'pgn') return 'pgn';
  if (ext === 'pdf') return 'pdf';
  if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext)) return 'video';
  return 'file';
}

/**
 * Get child-item counts for a list of folder IDs (uses server-side count, no full docs fetched).
 * Returns a Map: folderId → number.
 */
export const getLibraryFolderCounts = async (coachId, folderIds) => {
  if (!folderIds.length) return {};
  const counts = {};
  await Promise.all(
    folderIds.map(async (fid) => {
      try {
        const q = query(libraryRef(coachId), where('parentFolderId', '==', fid));
        const snap = await getCountFromServer(q);
        counts[fid] = snap.data().count;
      } catch {
        counts[fid] = 0;
      }
    })
  );
  return counts;
};

// ─── Read Operations ────────────────────────────────────────────────────────

/**
 * Get library items at a given folder level, sorted pinned-first then by order.
 */
export const getLibraryItems = async (coachId, parentFolderId = null) => {
  const q = query(
    libraryRef(coachId),
    where('parentFolderId', '==', parentFolderId),
    orderBy('pinned', 'desc'),
    orderBy('order', 'asc')
  );
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Enrich shop-linked folders with their product image
  const linkedFolders = items.filter((i) => i.type === 'folder' && i.linkedProductId);
  if (linkedFolders.length > 0) {
    const productFetches = linkedFolders.map((f) =>
      getDoc(doc(db, 'products', f.linkedProductId)).catch(() => null)
    );
    const productSnaps = await Promise.all(productFetches);
    const imageMap = {};
    productSnaps.forEach((snap, idx) => {
      if (snap?.exists()) {
        const pd = snap.data();
        imageMap[linkedFolders[idx].id] = pd.imageUrl || pd.preview_image || null;
      }
    });
    items.forEach((item) => {
      if (imageMap[item.id]) {
        item.productImageUrl = imageMap[item.id];
      }
    });
  }

  return items;
};

/**
 * Get a single library item.
 */
export const getLibraryItem = async (coachId, itemId) => {
  const snap = await getDoc(libraryDocRef(coachId, itemId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

/**
 * Walk the parentFolderId chain to build breadcrumbs.
 * Returns array from root → current: [{ id, title }, ...]
 */
export const getFolderBreadcrumbs = async (coachId, folderId) => {
  const crumbs = [];
  let currentId = folderId;

  while (currentId) {
    const item = await getLibraryItem(coachId, currentId);
    if (!item) break;
    crumbs.unshift({ id: item.id, title: item.title });
    currentId = item.parentFolderId || null;
  }

  return crumbs;
};

/**
 * Get all folders in the library (flat list for folder picker).
 */
export const getAllLibraryFolders = async (coachId) => {
  const q = query(
    libraryRef(coachId),
    where('type', '==', 'folder')
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
};

/**
 * Get pinned library items (for preview displays).
 * Excludes admin-only items since this is used on public-facing surfaces.
 */
export const getPinnedLibraryItems = async (coachId, maxItems = 6) => {
  const q = query(
    libraryRef(coachId),
    where('pinned', '==', true),
    orderBy('order', 'asc'),
    limit(maxItems + 5) // fetch extra to compensate for filtered admin items
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((item) => item.accessLevel !== ACCESS_LEVELS.ADMIN)
    .slice(0, maxItems);
};

/**
 * Batch-fetch multiple library items by their IDs.
 */
export const getLibraryItemsByIds = async (coachId, itemIds) => {
  if (!itemIds || itemIds.length === 0) return [];
  const results = await Promise.all(
    itemIds.map((id) => getLibraryItem(coachId, id))
  );
  return results.filter(Boolean);
};

// ─── Write Operations ───────────────────────────────────────────────────────

/**
 * Create a folder in the library.
 */
export const createLibraryFolder = async (coachId, data) => {
  const docRef = await addDoc(libraryRef(coachId), {
    type: 'folder',
    title: data.title || 'Untitled Folder',
    description: data.description || '',
    accessLevel: data.accessLevel || ACCESS_LEVELS.PUBLIC,
    allowedStudentIds: data.allowedStudentIds || [],
    parentFolderId: data.parentFolderId || null,
    pinned: false,
    order: data.order ?? 999,
    systemFolder: data.systemFolder || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Create a file item in the library (PGN, PDF, video, generic file).
 */
export const createLibraryItem = async (coachId, data) => {
  const docRef = await addDoc(libraryRef(coachId), {
    type: data.type || 'file',
    title: data.title || 'Untitled',
    description: data.description || '',
    accessLevel: data.accessLevel || ACCESS_LEVELS.PUBLIC,
    allowedStudentIds: data.allowedStudentIds || [],
    parentFolderId: data.parentFolderId || null,
    pinned: false,
    order: data.order ?? 999,
    storageUrl: data.storageUrl || '',
    downloadUrl: data.downloadUrl || '',
    fileName: data.fileName || '',
    fileSize: data.fileSize || 0,
    mimeType: data.mimeType || '',
    allowDownload: data.allowDownload === true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Create a puzzle item in the library.
 */
export const createLibraryPuzzle = async (coachId, data) => {
  const docRef = await addDoc(libraryRef(coachId), {
    type: 'puzzle',
    title: data.title || 'Untitled Puzzle',
    description: data.description || '',
    accessLevel: data.accessLevel || ACCESS_LEVELS.PUBLIC,
    allowedStudentIds: data.allowedStudentIds || [],
    parentFolderId: data.parentFolderId || null,
    pinned: false,
    order: data.order ?? 999,
    fen: data.fen || '',
    solutionMoves: data.solutionMoves || [],
    hint: data.hint || '',
    explanation: data.explanation || '',
    difficulty: data.difficulty || 'intermediate',
    toMove: data.toMove || 'w',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Create an instructions item in the library.
 */
export const createLibraryInstructions = async (coachId, data) => {
  const docRef = await addDoc(libraryRef(coachId), {
    type: 'instructions',
    title: data.title || 'Untitled Instructions',
    description: '',
    content: data.content || '',
    accessLevel: data.accessLevel || ACCESS_LEVELS.PUBLIC,
    allowedStudentIds: data.allowedStudentIds || [],
    parentFolderId: data.parentFolderId || null,
    pinned: false,
    order: data.order ?? 999,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Update an existing library item.
 */
export const updateLibraryItem = async (coachId, itemId, updates) => {
  await updateDoc(libraryDocRef(coachId, itemId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a library item.
 */
export const deleteLibraryItem = async (coachId, itemId) => {
  await deleteDoc(libraryDocRef(coachId, itemId));
};

/**
 * Batch update order values for reordering.
 */
export const reorderLibraryItems = async (coachId, orderedIds) => {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    batch.update(libraryDocRef(coachId, id), { order: index });
  });
  await batch.commit();
};

/**
 * Toggle the pinned state of a library item.
 */
export const toggleLibraryItemPin = async (coachId, itemId, pinned) => {
  await updateDoc(libraryDocRef(coachId, itemId), {
    pinned,
    updatedAt: serverTimestamp(),
  });
};

// ─── Storage ────────────────────────────────────────────────────────────────

/**
 * Upload a file to Firebase Storage under library/{coachId}/.
 * Returns { storageUrl, downloadUrl, fileName, fileSize, mimeType }.
 * Accepts an optional onProgress callback: (percent) => void.
 */
export const uploadLibraryFile = (coachId, file, onProgress) => {
  return new Promise((resolve, reject) => {
    // TODO: Enforce per-tier upload limits and storage quotas
    const maxSize = 3 * 1024 * 1024 * 1024; // 3 GB
    if (file.size > maxSize) {
      reject(new Error('File size too large. Max 3 GB.'));
      return;
    }

    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop().toLowerCase();
    const storagePath = `library/${coachId}/${timestamp}_${randomId}.${ext}`;
    const storageRef = ref(storage, storagePath);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        if (onProgress) onProgress(percent);
      },
      (error) => reject(error),
      async () => {
        const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({
          storageUrl: storagePath,
          downloadUrl,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type || `application/${ext}`,
        });
      }
    );
  });
};

// ─── System Folders ─────────────────────────────────────────────────────────

/**
 * Ensure the permanent "Puzzles" system folder exists at root level.
 * Returns the folder doc ID.
 */
export const ensurePuzzlesFolder = async (coachId) => {
  const q = query(
    libraryRef(coachId),
    where('systemFolder', '==', SYSTEM_FOLDER_PUZZLES),
    limit(1)
  );
  const snap = await getDocs(q);

  if (!snap.empty) return snap.docs[0].id;

  // Create the system folder
  const id = await createLibraryFolder(coachId, {
    title: 'Puzzles',
    description: 'Interactive puzzles for your students',
    accessLevel: ACCESS_LEVELS.PUBLIC,
    parentFolderId: null,
    order: 0,
    systemFolder: SYSTEM_FOLDER_PUZZLES,
  });

  return id;
};

/**
 * Ensure the "Start Here" system folder exists at root level with onboarding content.
 * This folder uses ADMIN access level so only the coach sees it.
 * Contains 6 instruction guides covering every paid feature + 1 demo puzzle.
 * Returns the folder doc ID.
 */
export const ensureStartHereFolder = async (coachId) => {
  const q = query(
    libraryRef(coachId),
    where('systemFolder', '==', SYSTEM_FOLDER_START_HERE),
    limit(1)
  );
  const snap = await getDocs(q);

  if (!snap.empty) return snap.docs[0].id;

  // Create the system folder
  const folderId = await createLibraryFolder(coachId, {
    title: 'Start Here',
    description: 'Your complete guide to ChessWeb.site — coaching, content, monetization, and building your website.',
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: null,
    order: 0,
    systemFolder: SYSTEM_FOLDER_START_HERE,
  });

  // ── 0. Welcome to ChessWeb.site (feature showcase) ──
  await createLibraryInstructions(coachId, {
    title: 'Welcome to ChessWeb.site',
    content: `Welcome to **ChessWeb.site** — the platform built for the chess industry. Coaches, professionals, and creators use it to run their entire business online.

You're reading an **Instructions** card — one of the most versatile content types in your library. Scroll down to see everything a single card can do.

## Interactive Chess Board

Embed playable games directly in your content. Students step through every move on a real board — no external tools needed. Here's Paul Morphy's legendary Opera Game:

\`\`\`chess
FEN: rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1
PGN: 1. e4 e5 2. Nf3 d6 3. d4 Bg4 4. dxe5 Bxf3 5. Qxf3 dxe5 6. Bc4 Nf6 7. Qb3 Qe7 8. Nc3 c6 9. Bg5 b5 10. Nxb5 cxb5 11. Bxb5+ Nbd7 12. O-O-O Rd8 13. Rxd7 Rxd7 14. Rd1 Qe6 15. Bxd7+ Nxd7 16. Qb8+ Nxb8 17. Rd8#
Title: The Opera Game — Morphy vs Duke Karl & Count Isouard (1858)
Show Solution: true
\`\`\`

## Lichess Study Embed

Already have studies on Lichess? Embed them directly into any Instructions card using the **Lichess Study** button in the editor toolbar. Students interact with the full study — moves, annotations, arrows — without ever leaving your site.

Here's a popular Opening Traps study embedded live:

<iframe width="600" height="371" src="https://lichess.org/study/embed/Of3mcPk8/NHPhHyxK" frameborder=0></iframe>

*To embed your own: open any Lichess study chapter, click Share, copy the iframe code, and paste it here.*

## Copyable Code Snippets

Use code blocks for anything students need to copy with one click — PGN notation, FEN strings, prep notes, or opening repertoire lines.

**PGN — Italian Game, Giuoco Piano Main Line:**

\`\`\`
1. e4 e5 2. Nf3 Nc6 3. Bc4 Bc5 4. c3 Nf6 5. d4 exd4 6. cxd4 Bb4+ 7. Bd2 Bxd2+ 8. Nbxd2 d5 9. exd5 Nxd5
\`\`\`

**FEN — Position after 9...Nxd5:**

\`\`\`
r1bqk2r/ppp2ppp/2n5/3n4/2BP4/5N2/PP1N1PPP/R2QK2R w KQkq - 0 10
\`\`\`

White has the isolated d4 pawn but active piece play. Black should target d4 and develop the c8 bishop.

## Full Text Formatting

Instructions also support **bold**, *italic*, __underline__, [links](https://chessweb.site), and headings at every level — everything you need for lesson plans, course outlines, homework assignments, announcements, or game annotations.

A single Instructions card can replace an entire lesson page. Combine boards, studies, copyable material, and formatted text into one cohesive document.

── WHAT'S NEXT ──

• Open the **Platform Guides** folder below for detailed walkthroughs of every feature — coaching, pricing, shop, and your website builder
• Try the **Demo Puzzle** to see interactive puzzles with hints, solutions, and progress tracking
• Head to **Dashboard > Academy** to set up your coaching profile
• Customize your site in **Dashboard > Website**

── HOW THIS FOLDER WORKS ──

This "Start Here" folder is set to **Admin Only**, so only you can see it. It's your private setup guide.

Want to turn it into a public feature showcase? Change the folder's access level to "Free" and everything inside becomes visible to visitors.

*Tip: You can edit, rearrange, or delete any of these items. They're yours.*`,
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: folderId,
    order: 0,
  });

  // ── 2. Platform Guides subfolder (demonstrates folder nesting) ──
  const guidesFolderId = await createLibraryFolder(coachId, {
    title: 'Platform Guides',
    description: 'Detailed walkthroughs of every ChessWeb.site feature — coaching, content, monetization, and your website.',
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: folderId,
    order: 2,
  });

  // ── 1a. Content Library ──
  await createLibraryInstructions(coachId, {
    title: 'Content Library',
    content: `You're in the Content Library right now. This is where you organize courses, study materials, and anything you want to share with your students.

── FOLDERS ──

Folders are the backbone of your library. A single course, a homework set, or a full semester curriculum — each lives inside a folder. You can nest folders for multi-week programs.

Create a folder by clicking "New Folder" in the toolbar above.

── PGN STUDIES ──

Upload any .pgn file and students get a built-in interactive chessboard. They can step through moves, explore variations, and read annotations directly in the browser — no extra software needed.

This works with any standard PGN, including exports from Lichess, Chess.com, or ChessBase.

── LICHESS STUDY EMBEDS ──

Already have studies on Lichess? Upload the chapter PGNs and students get the same interactive chessboard experience. Build chapter-by-chapter courses with annotated games, arrows, highlights, and commentary.

A straightforward way to bring existing teaching material onto your site.

── VIDEO LESSONS ──

Upload .mp4 or .mov files for recorded lessons, game reviews, or lectures. Videos play inline so students stay on your site while they watch.

Record once, and students can review on their own schedule.

── PUZZLES ──

Create custom puzzles with a starting position (FEN), solution moves, hints, and explanations. Set difficulty (easy, intermediate, hard) and student progress is tracked automatically.

Students see a real chessboard, attempt the solution, and get instant feedback. Try the demo puzzle at the bottom of this folder to see how it works.

── PDF HANDOUTS ──

Upload worksheets, opening cheat sheets, tournament prep packets, or reference material. Students can view PDFs inline, and you control whether downloads are allowed.

── INSTRUCTIONS ──

Text-based notes, lesson plans, or announcements — like the one you're reading. Useful for adding context at the top of a course folder: "Start with the video, then try the puzzles."

── COURSE-BUILDING WORKFLOW ──

A typical setup looks like this:
1. Create a folder (e.g., "Italian Game Masterclass")
2. Add an Instructions item at the top with an overview
3. Upload PGN studies for each lesson
4. Add puzzles for practice
5. Drop in a video recap
6. Set the folder's access level (Free, Members, Students, or link it to a Shop product)

Pin important items so they appear first. Drag to reorder. Hit "Preview" in the toolbar to see your library the way students see it.

── ACCESS LEVELS ──

Every item and folder has an access level:
• Free — Visible to everyone, no login required
• Members — Active subscribers only (pairs with Memberships)
• Students / Past Students — Anyone who has booked or purchased from you
• Specific Students — Hand-pick exactly who sees what
• Admin Only — Only visible to you on the dashboard

You can mix levels within a folder — make the first lesson Free as a sample and lock the rest behind a membership or Shop purchase.`,
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: guidesFolderId,
    order: 0,
  });

  // ── 1b. Coaching Academy ──
  await createLibraryInstructions(coachId, {
    title: 'Coaching Academy',
    content: `The Coaching Academy is a full scheduling and payment system built into your website. Students browse your offerings, book sessions, and pay — all in one place.

── GROUP LESSONS ──

Group lessons are scheduled classes that multiple students can join. You set the date, time, capacity, and price. Students see available lessons on your public coaching page and can register and pay in one click.

To create one: Dashboard > Academy > Lessons tab > New Lesson.

Each lesson gets its own video room powered by Daily.co — a real-time video call that runs directly in the browser. No Zoom links, no third-party apps. You click "Start," students click "Join."

── INDIVIDUAL (1-ON-1) SESSIONS ──

Individual sessions are private, one-student appointments. Schedule them manually for a specific student, or let students request sessions through your coaching page.

To schedule one: Dashboard > Academy > Sessions tab > New Session.

Individual sessions also get dedicated video rooms. Date, student, payment status, and notes are all tracked automatically.

── SCHEDULING ──

Your coaching schedule is visible on your website. Students can see upcoming group lessons and available slots. Past sessions archive automatically.

You manage everything from the Academy dashboard — reschedule, cancel, add notes, or post updates.

── STUDENT MANAGEMENT ──

Every student who books with you shows up in your student list with their booking history, credit balance, and session notes.

Dashboard > Academy > Students tab.

── COACH UPDATES ──

Post announcements that appear at the top of your coaching page — schedule changes, new offerings, holiday notices, or anything students should know.

── VIDEO ROOMS ──

Every lesson and session includes a built-in video room. No setup needed. The room activates when you start it, and students join with one click.

Screen sharing is supported — useful for walking through games, analyzing positions, or reviewing study material together.`,
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: guidesFolderId,
    order: 1,
  });

  // ── 1c. Credits, Memberships & Pricing ──
  await createLibraryInstructions(coachId, {
    title: 'Credits, Memberships & Pricing',
    content: `You have two ways to charge for coaching: credit packages (one-time purchases) and memberships (recurring subscriptions). Use one or both.

── CREDIT PACKAGES ──

Credits are the default payment method. Students buy a bundle of session credits upfront, and each booking deducts from their balance.

You configure your own packages — for example:
• 1 session — $40
• 4 sessions — $140 (save $20)
• 7 sessions — $230 (save $50)

Students purchase credits from your coaching page. When they book a lesson or session, credits deduct automatically. You set the price per session type.

To configure: Dashboard > Academy > Pricing tab.

No more chasing payments after each lesson. Students prepay, sessions deduct, and the balance is always visible to both of you.

── MEMBERSHIPS ──

Memberships are monthly subscriptions. You define what each tier includes — a set number of credits per month, access to Members-only library content, priority booking, or any combination.

When a student subscribes, credits are added to their account each billing cycle automatically. A membership with 4 credits/month means those credits appear on renewal day without any action from you.

Memberships pair well with the Content Library. Set folders to "Members" access and subscribers get exclusive material — study files, recorded lessons, puzzle sets — as long as they stay subscribed.

── PRICING STRATEGY ──

A common approach: offer a free or discounted trial lesson, then a membership that bundles monthly credits with Members-only library access. Students who aren't ready to commit can still buy individual credit packages.

── PLATFORM FEES ──

ChessWeb takes a small percentage on coaching, shop, and tournament transactions:
• $9.99/month plan (Master): 10% platform fee
• $29.99/month plan (Grandmaster): 5% platform fee
• Donations: Always free — no platform fee

Fees are handled transparently through Stripe. You receive the remainder directly to your connected account.

── STRIPE CONNECT ──

To receive payments, connect your Stripe account from Dashboard > Settings > Payments. It takes about 2 minutes. Once connected, coaching payments, shop purchases, and membership subscriptions all flow directly to your bank account.`,
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: guidesFolderId,
    order: 2,
  });

  // ── 1d. Shop & Monetization ──
  await createLibraryInstructions(coachId, {
    title: 'Shop & Monetization',
    content: `The Shop is where you sell digital products and courses, with built-in checkout powered by Stripe.

── DIGITAL PRODUCTS ──

Create products from Dashboard > Shop > New Product. Add a title, description, price, and cover image. Products appear on your site's Shop page where students can purchase directly.

A digital product can be anything: a PGN collection, a PDF workbook, a video series, or a full course.

── SHOP-LIBRARY LINKING (PAID COURSES) ──

This is where the Library and Shop connect. Link any Library folder to a Shop product and that folder becomes a paid course — students who purchase the product automatically unlock the folder and everything inside.

To link: open a folder's action menu (three dots) > "Link to Product" > select your product.

Build the content in your Library, create a product in your Shop, link them together, done. The platform handles checkout, access control, and delivery.

── MEMBERSHIPS AS PRODUCTS ──

Membership subscriptions also live in the Shop. Students subscribe and get recurring credits plus access to Members-only Library content — all through the same Stripe checkout flow.

── DONATIONS ──

Enable a donation button on your site so visitors can support your work directly. You can set suggested amounts or let supporters choose their own.

Configure from Dashboard > Settings > Donations.

── REFERRAL PROGRAM ──

Share your unique referral link and earn 20% lifetime commission on every paying customer you refer to the platform.

Track referrals from Dashboard > Referrals — who signed up, when they converted, and your earnings.

── STRIPE CONNECT SETUP ──

All monetization runs through Stripe Connect. If you haven't connected yet:
1. Go to Dashboard > Settings > Payments
2. Click "Connect Stripe Account"
3. Complete the Stripe onboarding (about 2 minutes)

Once connected, all payments — coaching, shop, memberships, donations — deposit directly to your bank account.`,
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: guidesFolderId,
    order: 3,
  });

  // ── 1e. Website Builder & Going Live ──
  await createLibraryInstructions(coachId, {
    title: 'Website Builder & Going Live',
    content: `Your website is built with a block editor — add, remove, and rearrange pre-built sections to assemble your site.

── BLOCK EDITOR ──

Open it from Dashboard > Website. Your site is made up of blocks, where each block is a section of your page. Customize each one, reorder them, and preview before publishing.

The editor uses a draft/publish model: changes are saved as drafts until you explicitly hit Publish. Nothing goes live until you say so.

── KEY BLOCKS ──

• Banner — Hero section with headline, subtitle, and call-to-action button. The first thing visitors see.
• Coaching — Shows your coaching offerings, schedule, and booking buttons. Pulls from your Academy setup automatically.
• Library Showcase — Displays pinned items from your Content Library on your homepage.
• Blog — Blog posts as cards. Write them from Dashboard > Blog.
• Contact — A contact form that delivers messages to your inbox (Dashboard > Messages).
• Social Links — Icons linking to your Chess.com, Lichess, YouTube, Twitter, Instagram, and other profiles.
• Calendly — Embed your Calendly scheduling widget directly on your page.

There are 20+ block types total. Browse them in the editor to find what works for your site.

── THEMES ──

Pick a color theme that fits your brand. Themes control background colors, text, accents, and card styles. Switch anytime from the Website editor.

── CUSTOM DOMAINS ──

Every site gets a free URL at chessweb.site/me/yourname. Premium users also get a subdomain: yourname.chessweb.site.

Want your own .com? Connect a custom domain from Dashboard > Settings > Domain.

── HEADER & FOOTER ──

Customize your header with your name, logo, navigation links, and a call-to-action button. The footer includes social links, copyright, and optional custom text.

── ANNOUNCEMENT BAR ──

A colored bar at the very top of your site for time-sensitive messages — "New group lesson every Thursday!" or "50% off first session." Toggle it from the header editor.

── DRAFT / PUBLISH ──

Every edit saves as a draft. Preview to see how it looks, then Publish when you're ready. Made a mistake? Revert to the last published version.

You can experiment freely without affecting your live site.

── GOING LIVE CHECKLIST ──

1. Fill out your profile (Dashboard > Profile) — name, photo, bio, chess details
2. Set up coaching offerings and pricing (Dashboard > Academy)
3. Add content to your Library (at least one folder with a few items)
4. Customize your website blocks (Dashboard > Website)
5. Connect Stripe for payments (Dashboard > Settings > Payments)
6. Preview your site, then Publish
7. Share your URL — you're live`,
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: guidesFolderId,
    order: 4,
  });

  // ── 1. Demo Puzzle — Scholar's Mate ──
  await createLibraryPuzzle(coachId, {
    title: 'Demo Puzzle — Scholar\'s Mate',
    description: 'A classic beginner pattern. This is what students see when they open a puzzle — an interactive chessboard with hints, solution validation, and progress tracking.',
    fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
    solutionMoves: ['Qxf7#'],
    hint: 'The queen and bishop are both aiming at the same weak square...',
    explanation: 'Qxf7# is checkmate! The queen is protected by the bishop on c4, and the king has no escape squares. This is Scholar\'s Mate — one of the first patterns every beginner should recognize.',
    difficulty: 'easy',
    toMove: 'w',
    accessLevel: ACCESS_LEVELS.ADMIN,
    parentFolderId: folderId,
    order: 1,
  });

  return folderId;
};

// ─── Puzzle Progress ────────────────────────────────────────────────────────

const progressDocRef = (coachId, puzzleId, userId) =>
  doc(db, 'coaches', coachId, 'library', puzzleId, 'progress', userId);

/**
 * Get puzzle progress for a specific user.
 */
export const getPuzzleProgress = async (coachId, puzzleId, userId) => {
  const snap = await getDoc(progressDocRef(coachId, puzzleId, userId));
  if (!snap.exists()) return null;
  return snap.data();
};

/**
 * Update puzzle progress for a user.
 */
export const updatePuzzleProgress = async (coachId, puzzleId, userId, data) => {
  await setDoc(progressDocRef(coachId, puzzleId, userId), {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// ─── Shop-Library Linking ───────────────────────────────────────────────────

/**
 * Link a library folder to a shop product (and vice versa) via batch write.
 */
export const linkFolderToProduct = async (coachId, folderId, productId) => {
  const batch = writeBatch(db);
  batch.update(libraryDocRef(coachId, folderId), {
    linkedProductId: productId,
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, 'products', productId), {
    libraryFolderId: folderId,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
};

/**
 * Unlink a library folder from a shop product via batch write.
 */
export const unlinkFolderFromProduct = async (coachId, folderId, productId) => {
  const batch = writeBatch(db);
  batch.update(libraryDocRef(coachId, folderId), {
    linkedProductId: null,
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, 'products', productId), {
    libraryFolderId: null,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
};

/**
 * Get active digital products for a given vendor (coach).
 */
export const getCoachDigitalProducts = async (vendorId) => {
  const q = query(
    collection(db, 'products'),
    where('vendorId', '==', vendorId),
    where('isActive', '==', true)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export { detectFileType };
